import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import Button, { ButtonProps } from '@platform-ui-common/components/Button/index.js';
import { ChipSize } from '@platform-ui-common/components/Chip/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import ApplicantRoutes from '@platform-ui/application/utils/ApplicantRoutes.js';
import { Box, Dialog, Flex, Heading, Text } from '@radix-ui/themes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import PrefilledChip from '../PrefilledChip/index.js';
import ReferralChip from '../ReferralChip/index.js';

type StatusBlockChipType = 'referral' | 'prefilled';

export interface StatusBlockProps {
  id?: string;
  title?: string;
  label: string;
  message: string;
  button?: Pick<ButtonProps, 'children' | 'link' | 'variant'>;
  chips?: StatusBlockChipType[];
  isAnonymousUser?: boolean;
}

export function StatusBlockChip({ chip }: { chip: StatusBlockChipType }): JSX.Element {
  switch (chip) {
    case 'referral':
      return <ReferralChip variant="white" />;
    case 'prefilled':
      return <PrefilledChip variant="white" size={ChipSize.S} plural />;
    default:
      return assertExhaustive(chip);
  }
}

export default function StatusBlock({
  id,
  title,
  label: status,
  message,
  button,
  chips,
  isAnonymousUser,
}: StatusBlockProps): JSX.Element {
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const navigate = useNavigate();
  const buttonLink = button?.link;

  const continueButtonAction = (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    setOpenConfirmation(false);
    if (buttonLink) {
      const isExternal = /^https?:\/\//i.test(buttonLink);
      if (isExternal) {
        window.location.assign(buttonLink);
      } else {
        navigate(buttonLink);
      }
    }
  };

  const handleLinkAccount = () => {
    setOpenConfirmation(false);
    navigate(ApplicantRoutes.PROFILE);
  };

  const hasChips = (chips?.length ?? 0) > 0;

  return (
    <div data-cy={id} className="w-full flex flex-col items-center text-center bg-grayBackground">
      <Flex direction="column" maxWidth="36rem" align="center" py="4" px="4">
        {hasChips && (
          <Flex gap="2" mb="2">
            {chips?.map((chip) => (
              <StatusBlockChip key={chip} chip={chip} />
            ))}
          </Flex>
        )}
        {title && <Heading size="5">{title}</Heading>}
        <Box mb="2">
          <Text size="3" weight="medium">
            {status}
          </Text>
        </Box>
        <div className="flex flex-col gap-4">
          <Markdown variant="body">{message}</Markdown>
        </div>
        {button && (
          <div className="mt-3 mb-1">
            {isAnonymousUser ? (
              <Dialog.Root open={openConfirmation} onOpenChange={setOpenConfirmation}>
                <Dialog.Trigger>
                  <Button id={id} variant={button.variant}>
                    {button.children}
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content size="2" maxWidth="460px">
                  <Dialog.Title>Continue as a guest?</Dialog.Title>
                  <Dialog.Description>
                    You are using a guest account. You can continue, but to keep access across
                    devices and receive updates, you will need to provide and verify an email.
                  </Dialog.Description>
                  <Flex justify="end" gap="3" mt="4">
                    <Button variant="outline" shrink onClick={handleLinkAccount}>
                      Link your account
                    </Button>
                    <Button shrink onClick={continueButtonAction}>
                      Continue
                    </Button>
                  </Flex>
                </Dialog.Content>
              </Dialog.Root>
            ) : (
              <Button id={id} {...button} />
            )}
          </div>
        )}
      </Flex>
    </div>
  );
}
